import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import {
  Plus, Trash2, Send, Calendar, DollarSign, FileText,
  User, Mail, Building, ChevronRight, Check, X, AlertCircle,
  Paperclip, File
} from 'lucide-react';
import InvoiceSuccessModal from '../common/InvoiceSuccessModal';
import { extractBusinessProfile } from '../../utils/contactNormalization';
import { checkStripeMinimum, getMinimumAmountError } from '../../utils/stripeMinimums';

const MobileSendInvoice = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const businessProfile = useMemo(() => extractBusinessProfile(user), [user]);
  const businessWarnings = useMemo(() => {
    const issues = [];
    if (!businessProfile.companyName?.trim()) issues.push('company name');
    if (!businessProfile.address?.trim()) issues.push('street address');
    if (!businessProfile.city?.trim()) issues.push('city');
    if (!businessProfile.zipCode?.trim()) issues.push('postal code');
    if (!businessProfile.country?.trim()) issues.push('country');
    return issues;
  }, [businessProfile]);
  const [step, setStep] = useState(1); // 1: Recipient, 2: Items, 3: Details
  const [recipientType, setRecipientType] = useState('user');
  const [selectedUser, setSelectedUser] = useState(null);
  const [emailLookupLoading, setEmailLookupLoading] = useState(false);
  const [invoice, setInvoice] = useState({
    recipient: {
      email: '',
      firstName: '',
      lastName: '',
      companyName: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United Kingdom'
    },
    lineItems: [{
      description: '',
      quantity: 1,
      unitPrice: 0,
      taxRate: 0,
      discount: 0
    }],
    dueDate: '',
    currency: 'GBP',
    notes: '',
    poNumber: '',
    terms: ''
  });
  
  // Early payment discount state
  const [earlyPaymentDiscount, setEarlyPaymentDiscount] = useState({
    enabled: false,
    discountAmount: '',
    discountPercentage: '',
    timeframeDays: 7
  });
  
  // Custom invoice number state
  const [customInvoiceNumber, setCustomInvoiceNumber] = useState('');
  const [invoiceNumberError, setInvoiceNumberError] = useState('');
  const [suggestedInvoiceNumber, setSuggestedInvoiceNumber] = useState('');

  // Attachments
  const [attachments, setAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState(null);

  const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024; // 10 MB
  const SUPPORTED_FILE_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
    'text/plain'
  ];
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successInvoiceData, setSuccessInvoiceData] = useState(null);
  const [error, setError] = useState(null);
  
  // Guest contact search state
  const [guestContactSearch, setGuestContactSearch] = useState('');
  const [guestContactResults, setGuestContactResults] = useState([]);
  const [guestContactSearchLoading, setGuestContactSearchLoading] = useState(false);
  const [showGuestContactDropdown, setShowGuestContactDropdown] = useState(false);

  // Auto-populate from contactId URL parameter
  useEffect(() => {
    const contactId = searchParams.get('contactId');
    if (contactId) {
      fetchAndPopulateContact(contactId);
    }
  }, [searchParams]);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (window.emailLookupTimeout) {
        clearTimeout(window.emailLookupTimeout);
      }
    };
  }, []);

  // Set default country to "United Kingdom" when switching to guest recipient type
  useEffect(() => {
    if (recipientType === 'guest' && (!invoice.recipient.country || invoice.recipient.country === 'GB' || invoice.recipient.country === '')) {
      setInvoice(prev => ({
        ...prev,
        recipient: {
          ...prev.recipient,
          country: 'United Kingdom'
        }
      }));
    }
  }, [recipientType]);

  // Expose test function to window for debugging
  useEffect(() => {
    window.testMobileAutoPopulate = (email = 'amylily28@gmail.com') => {
      console.log('🧪 Mobile - Testing auto-population with email:', email);
      if (recipientType === 'user') {
        lookupUserByEmail(email);
      } else {
        console.log('❌ Mobile - Please select "Receevit® User" recipient type first');
        setRecipientType('user');
        setTimeout(() => lookupUserByEmail(email), 100);
      }
    };
    
    return () => {
      delete window.testMobileAutoPopulate;
    };
  }, [recipientType]);

  // Fetch contact and auto-populate form
  const fetchAndPopulateContact = async (contactId) => {
    try {
      console.log('📋 Mobile - Fetching contact:', contactId);
      const response = await api.get(`/contacts/${contactId}`);
      const contact = response.data.contact;
      
      console.log('✅ Mobile - Contact fetched:', contact);
      
      // Check if contact is a Receevit® user or guest
      if (contact.is_receevit_user && contact.receevit_user_id) {
        // Switch to Receevit® User type and populate
        setRecipientType('user');
        setSelectedUser({
          id: contact.receevit_user_id,
          email: contact.email,
          firstName: contact.first_name,
          lastName: contact.last_name,
          companyName: contact.company_name
        });
        setInvoice(prev => ({
          ...prev,
          recipient: {
            email: contact.email,
            firstName: contact.first_name,
            lastName: contact.last_name,
            companyName: contact.company_name,
            address: contact.address_line1 || '',
            city: contact.city,
            state: contact.state_province || '',
            zipCode: contact.postal_code,
            country: contact.country || 'United Kingdom'
          }
        }));
      } else {
        // Switch to Guest type and populate
        setRecipientType('guest');
        setInvoice(prev => ({
          ...prev,
          recipient: {
            companyName: contact.company_name,
            firstName: contact.first_name,
            lastName: contact.last_name,
            email: contact.email,
            address: contact.address_line1 || '',
            city: contact.city,
            state: contact.state_province || '',
            zipCode: contact.postal_code,
            country: contact.country || 'United Kingdom'
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching contact:', error);
      setError('Failed to load contact details');
    }
  };

  // Search guest contacts
  const searchGuestContacts = async (query) => {
    if (!query || query.length < 2) {
      setGuestContactResults([]);
      setShowGuestContactDropdown(false);
      return;
    }
    
    try {
      setGuestContactSearchLoading(true);
      const response = await api.get(`/contacts/search?query=${encodeURIComponent(query)}&type=guest`);
      setGuestContactResults(response.data.contacts || []);
      setShowGuestContactDropdown(true);
    } catch (error) {
      console.error('Error searching guest contacts:', error);
      setGuestContactResults([]);
    } finally {
      setGuestContactSearchLoading(false);
    }
  };

  // Handle guest contact selection
  const handleSelectGuestContact = (contact) => {
    setInvoice(prev => ({
      ...prev,
      recipient: {
        companyName: contact.company_name,
        firstName: contact.first_name,
        lastName: contact.last_name,
        email: contact.email,
        address: contact.address_line1 || '',
        city: contact.city,
        state: contact.state_province || '',
        zipCode: contact.postal_code,
        country: contact.country || 'GB'
      }
    }));
    setGuestContactSearch('');
    setShowGuestContactDropdown(false);
  };

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length < 100;
  };

  // Email lookup function
  const lookupUserByEmail = async (email) => {
    // Validate email format and length
    const trimmedEmail = email?.trim();
    if (!trimmedEmail || recipientType !== 'user' || !isValidEmail(trimmedEmail)) {
      console.log('❌ Mobile - Invalid email or recipient type:', { email: trimmedEmail, recipientType });
      setSelectedUser(null);
      return;
    }
    
    console.log('🔍 Mobile - Looking up user with email:', trimmedEmail);
    console.log('🔍 Mobile - Current recipient type:', recipientType);
    setEmailLookupLoading(true);
    
    try {
      // First try to lookup registered user
      // Note: api interceptor adds /api/ prefix automatically, so we use /invoices/lookup-user/ not /api/invoices/lookup-user/
      const endpoint = `/invoices/lookup-user/${encodeURIComponent(trimmedEmail)}`;
      console.log('📡 Mobile - Calling API endpoint:', endpoint);
      const response = await api.get(endpoint);
      console.log('📡 Mobile - Full API Response:', response);
      console.log('📡 Mobile - API Response data:', response.data);
      console.log('📡 Mobile - API Response status:', response.status);
      
      if (response.data.success && response.data.user) {
        const user = response.data.user;
        console.log('✅ Mobile - Registered user found:', user);

        const normalizedStatus = String(user.status || '').toLowerCase();
        const normalizedSubscriptionStatus = String(user.subscriptionStatus || '').toLowerCase();
        const isReceevitInactive =
          normalizedStatus === 'inactive' ||
          normalizedStatus === 'suspended' ||
          normalizedSubscriptionStatus === 'cancelled';

        if (isReceevitInactive) {
          console.log('⚠️ Mobile - Registered user is inactive. Switching to guest invoice flow.');
          setSelectedUser(null);
          setRecipientType('guest');
          setInvoice(prev => ({
            ...prev,
            recipient: {
              companyName: user.companyName || prev.recipient.companyName || '',
              firstName: user.firstName || '',
              lastName: user.lastName || '',
              email: user.email,
              address: user.address || prev.recipient.address || '',
              city: user.city || prev.recipient.city || '',
              state: user.state || prev.recipient.state || '',
              zipCode: user.zipCode || prev.recipient.zipCode || '',
              country: user.country || prev.recipient.country || 'United Kingdom'
            }
          }));
          return;
        }
        
        // Set selected user for reference
        setSelectedUser(user);
        
        // Auto-populate form fields
        setInvoice(prev => ({
          ...prev,
          recipient: {
            ...prev.recipient,
            companyName: user.companyName || '',
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email,
            // Optionally populate address if available
            address: user.address || prev.recipient.address || '',
            city: user.city || prev.recipient.city || '',
            state: user.state || prev.recipient.state || '',
            zipCode: user.zipCode || prev.recipient.zipCode || '',
            country: user.country || prev.recipient.country || 'GB'
          }
        }));
        
        console.log('✅ Mobile - Auto-population successful for:', user.firstName, user.lastName);
        
      } else {
        console.log('❌ Mobile - Registered user lookup failed, trying contacts...');
        
        // Fallback: Try to find contact in contacts list
        try {
          const contactsResponse = await api.get('/contacts');
          console.log('📡 Mobile - Contacts API Response:', contactsResponse.data);
          
          if (contactsResponse.data.success && contactsResponse.data.data?.contacts) {
            const contact = contactsResponse.data.data.contacts.find(c => 
              c.email && c.email.toLowerCase() === email.toLowerCase()
            );
            
            if (contact) {
              console.log('✅ Mobile - Contact found:', contact);
              
              // Set selected user for reference (using contact data)
              setSelectedUser({
                id: contact.id,
                firstName: contact.first_name,
                lastName: contact.last_name,
                companyName: contact.company_name,
                email: contact.email,
                address: contact.address_line1 || contact.address || '',
                city: contact.city || '',
                state: contact.state_province || contact.state || '',
                zipCode: contact.postal_code || '',
                country: contact.country || 'United Kingdom',
                isContact: true // Flag to indicate this is from contacts
              });
              
              // Auto-populate form fields from contact
              setInvoice(prev => ({
                ...prev,
                recipient: {
                  ...prev.recipient,
                  companyName: contact.company_name || '',
                  firstName: contact.first_name || '',
                  lastName: contact.last_name || '',
                  email: email,
                  address: contact.address_line1 || contact.address || prev.recipient.address || '',
                  city: contact.city || prev.recipient.city || '',
                  state: contact.state_province || contact.state || prev.recipient.state || '',
                  zipCode: contact.postal_code || prev.recipient.zipCode || '',
                  country: contact.country || prev.recipient.country || 'GB'
                }
              }));
              
              console.log('✅ Mobile - Auto-population successful from contacts for:', contact.first_name, contact.last_name);
              return; // Exit early on success
            }
          }
        } catch (contactError) {
          console.log('❌ Mobile - Contacts lookup failed:', contactError);
        }
        
        // If both registered user and contact lookup failed
        console.log('❌ Mobile - No user or contact found for email:', email);
        setSelectedUser(null);
      }
    } catch (error) {
      console.error('❌ Mobile - User lookup error:', error);
      
      // Handle different types of errors
      if (error.response?.status === 404) {
        console.log('👤 Mobile - User not found for email:', email);
        setSelectedUser(null);
      } else if (error.response?.status === 401) {
        console.error('🔐 Mobile - Authentication error - token may be invalid');
      } else if (error.response?.status === 403) {
        console.error('🚫 Mobile - Access denied - check permissions');
      } else {
        console.error('🌐 Mobile - Network or server error:', error.message);
      }
    } finally {
      setEmailLookupLoading(false);
    }
  };

  const calculateTotal = () => {
    return invoice.lineItems.reduce((sum, item) => {
      const subtotal = item.quantity * item.unitPrice;
      const discountAmount = subtotal * (item.discount / 100);
      const afterDiscount = subtotal - discountAmount;
      const taxAmount = afterDiscount * (item.taxRate / 100);
      return sum + afterDiscount + taxAmount;
    }, 0);
  };

  const addLineItem = () => {
    setInvoice({
      ...invoice,
      lineItems: [...invoice.lineItems, { description: '', quantity: 1, unitPrice: 0, taxRate: 0, discount: 0 }]
    });
  };

  const removeLineItem = (index) => {
    setInvoice({
      ...invoice,
      lineItems: invoice.lineItems.filter((_, i) => i !== index)
    });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...invoice.lineItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setInvoice({ ...invoice, lineItems: newItems });
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(attachment => attachment.id !== id));
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) {
      return;
    }

    for (const file of Array.from(files)) {
      try {
        if (file.size > MAX_ATTACHMENT_SIZE) {
          setAttachmentError(`"${file.name}" is larger than 10MB. Please upload a smaller file.`);
          continue;
        }

        const normalizedType = file.type || '';
        const isSupported =
          SUPPORTED_FILE_TYPES.includes(normalizedType) ||
          (normalizedType === '' && /\.(pdf|docx?|xlsx?|csv|txt|jpg|jpeg|png)$/i.test(file.name));

        if (!isSupported) {
          setAttachmentError(`"${file.name}" is not a supported file type.`);
          continue;
        }

        setAttachmentError(null);

        const uploadResponse = await api.post('/files/upload-url', {
          fileName: file.name,
          fileType: normalizedType || 'application/octet-stream',
          fileSize: file.size,
          category: 'invoice'
        });

        if (!uploadResponse.data?.success) {
          setAttachmentError(`Failed to prepare upload for ${file.name}`);
          continue;
        }

        const { uploadUrl, fileKey } = uploadResponse.data;

        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': normalizedType || 'application/octet-stream'
          }
        });

        const newAttachment = {
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: normalizedType || 'file',
          s3Key: fileKey,
          uploadedAt: new Date().toISOString()
        };

        setAttachments(prev => [...prev, newAttachment]);
      } catch (uploadError) {
        console.error('Mobile attachment upload failed:', uploadError);
        setAttachmentError(`Failed to upload ${file.name}. Please try again.`);
      }
    }
  };

  const handleSendInvoice = async () => {
    try {
      setLoading(true);
      setError(null);

      // 🛡️ EMAIL VERIFICATION GUARD: Prevent unverified users from sending invoices
      // Check if emailVerified is NOT true (catches false, undefined, null)
      if (user?.emailVerified !== true) {
        setError('You must verify your email address before sending invoices. Please check your email for the verification link.');
        setLoading(false);
        // Scroll to top to show the banner
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // 🛡️ STRIPE CONNECT GUARD: Check if user has connected Stripe account
      try {
        const stripeStatus = await api.get('/stripe-connect/account-status');
        
        // ✅ FIX: Backend returns account object directly, not hasConnectedAccount field
        const hasAccount = !!stripeStatus.data.account;
        const isFullyConnected = stripeStatus.data.account && 
                                 stripeStatus.data.account.chargesEnabled === true &&
                                 stripeStatus.data.account.payoutsEnabled === true;
        
        if (stripeStatus.data.success && !hasAccount) {
          setError('You need to connect your Stripe account to receive payments. Please go to Settings → Stripe Connect.');
          setLoading(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        } else if (hasAccount && !isFullyConnected) {
          setError('Your Stripe account setup is incomplete. Please complete the setup in Settings.');
          setLoading(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      } catch (stripeCheckError) {
        console.log('⚠️ Could not check Stripe status (non-critical):', stripeCheckError.message);
        // Continue anyway - backend will validate
      }

      const missingSenderFields = [];
      if (!businessProfile.companyName?.trim()) {
        missingSenderFields.push('company name');
      }
      if (!businessProfile.address?.trim()) missingSenderFields.push('street address');
      if (!businessProfile.city?.trim()) missingSenderFields.push('city');
      if (!businessProfile.zipCode?.trim()) missingSenderFields.push('postal code');
      if (!businessProfile.country?.trim()) missingSenderFields.push('country');

      if (missingSenderFields.length > 0) {
        setError(`Please update your business profile in Settings before sending invoices. Missing: ${missingSenderFields.join(', ')}`);
        setLoading(false);
        return;
      }

      // Validate due date is not in the past
      if (!invoice.dueDate) {
        setError('Due date is required');
        setLoading(false);
        return;
      }
      
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
      const selectedDueDate = new Date(invoice.dueDate);
      selectedDueDate.setHours(0, 0, 0, 0);
      
      if (selectedDueDate < today) {
        setError('Due date cannot be in the past. Please select today or a future date.');
        setLoading(false);
        return;
      }

      // Validate invoice number format (same as desktop)
      if (invoiceNumberError) {
        setError('Please fix the invoice number format');
        setLoading(false);
        return;
      }

      // Validate Stripe minimum amount before creating invoice
      const invoiceTotal = calculateTotal();
      const minCheck = checkStripeMinimum(invoiceTotal, invoice.currency);
      if (minCheck.isBelowMinimum) {
        setError(getMinimumAmountError(invoiceTotal, invoice.currency));
        setLoading(false);
        return;
      }

      // Prepare payload based on recipient type
      let payload;
      
      if (recipientType === 'user' && selectedUser) {
        // Send to registered user
        payload = {
          receiverId: selectedUser.id,
          items: invoice.lineItems.map(item => ({
            description: item.description,
            quantity: parseFloat(item.quantity),
            unitPrice: parseFloat(item.unitPrice),
            taxRate: parseFloat(item.taxRate || 0),
            discount: parseFloat(item.discount || 0)
          })),
          dueDate: invoice.dueDate,
          currency: invoice.currency,
          notes: invoice.notes,
          poNumber: invoice.poNumber,
          terms: invoice.terms,
          earlyPaymentDiscount: earlyPaymentDiscount.enabled ? {
            discountAmount: earlyPaymentDiscount.discountAmount ? parseFloat(earlyPaymentDiscount.discountAmount) : null,
            discountPercentage: earlyPaymentDiscount.discountPercentage ? parseFloat(earlyPaymentDiscount.discountPercentage) : null,
            timeframeDays: earlyPaymentDiscount.timeframeDays
          } : null,
          attachments: attachments.map(att => ({
            name: att.name,
            size: att.size,
            type: att.type,
            s3Key: att.s3Key,
            uploadedAt: att.uploadedAt
          })),
          customInvoiceNumber: customInvoiceNumber
        };
      } else {
        // Send to guest
        payload = {
          recipient: invoice.recipient,
          lineItems: invoice.lineItems.map(item => ({
            description: item.description,
            quantity: parseFloat(item.quantity),
            unitPrice: parseFloat(item.unitPrice),
            taxRate: parseFloat(item.taxRate || 0),
            discount: parseFloat(item.discount || 0)
          })),
          dueDate: invoice.dueDate,
          currency: invoice.currency,
          notes: invoice.notes,
          poNumber: invoice.poNumber,
          terms: invoice.terms,
          attachments: attachments.map(att => ({
            name: att.name,
            size: att.size,
            type: att.type,
            s3Key: att.s3Key,
            uploadedAt: att.uploadedAt
          })),
          customInvoiceNumber: customInvoiceNumber
        };
      }

      const response = await api.post('/invoices', payload);
      
      if (response.data.success) {
        // Store invoice data for modal
        setSuccessInvoiceData(response.data.data || {
          invoiceNumber: customInvoiceNumber,
          total: calculateTotal(),
          currency: invoice.currency,
          dueDate: invoice.dueDate
        });
        
        // Show success modal
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      
      // Handle specific error cases with user-friendly messages
      if (error.response?.data?.code === 'AMOUNT_BELOW_MINIMUM') {
        // Show detailed minimum amount error message
        const errorMessage = error.response.data.details || error.response.data.error;
        setError(errorMessage || 'Invoice amount is below the minimum required amount.');
      } else if (error.response?.data?.error) {
        // Show backend error message with details if available
        const errorMsg = error.response.data.error;
        const details = error.response.data.details ? `\n\n${error.response.data.details}` : '';
        setError(errorMsg + details);
      } else if (error.response?.data?.message) {
        // Show backend message
        setError(error.response.data.message);
      } else if (error.message) {
        // Show error message
        setError(error.message);
      } else {
        // Generic fallback
        setError('Failed to send invoice. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const canProceedToStep2 = () => {
    const hasRecipientBasics =
      invoice.recipient.email &&
      invoice.recipient.firstName &&
      invoice.recipient.lastName;

    // Check recipient address (required for invoice)
    const hasRecipientAddress =
      invoice.recipient.address?.trim() &&
      invoice.recipient.city?.trim() &&
      invoice.recipient.country?.trim() &&
      invoice.recipient.zipCode?.trim();

    // Check sender business address (required for invoice sender info)
    const hasBusinessAddress =
      businessProfile.address &&
      businessProfile.city &&
      businessProfile.country &&
      businessProfile.zipCode;

    const hasCompanyName = !!businessProfile.companyName?.trim();

    return hasRecipientBasics && hasRecipientAddress && hasBusinessAddress && hasCompanyName;
  };

  const canProceedToStep3 = () => {
    return invoice.lineItems.length > 0 && 
           invoice.lineItems.every(item => item.description && item.quantity > 0 && item.unitPrice > 0);
  };

  const canSend = () => {
    // Check recipient address (required for invoice)
    const hasRecipientAddress =
      invoice.recipient.address?.trim() &&
      invoice.recipient.city?.trim() &&
      invoice.recipient.country?.trim() &&
      invoice.recipient.zipCode?.trim();

    // Check sender business address (required for invoice sender info)
    const hasBusinessAddress =
      businessProfile.address &&
      businessProfile.city &&
      businessProfile.country &&
      businessProfile.zipCode;

    const hasCompanyName = !!businessProfile.companyName?.trim();

    // Validate due date is not in the past
    let hasValidDueDate = false;
    if (invoice.dueDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDueDate = new Date(invoice.dueDate);
      selectedDueDate.setHours(0, 0, 0, 0);
      hasValidDueDate = selectedDueDate >= today;
    }

    // Validate Stripe minimum amount
    const invoiceTotal = calculateTotal();
    const minCheck = checkStripeMinimum(invoiceTotal, invoice.currency);
    const meetsMinimumAmount = !minCheck.isBelowMinimum;

    return (
      canProceedToStep2() &&
      canProceedToStep3() &&
      invoice.dueDate &&
      hasValidDueDate &&
      customInvoiceNumber.trim() &&
      !invoiceNumberError && // Check validation error (same as desktop)
      hasRecipientAddress &&
      hasBusinessAddress &&
      hasCompanyName &&
      meetsMinimumAmount &&
      !attachmentError
    );
  };

  // Reset form function
  const resetForm = () => {
    setInvoice({
      recipient: {
        email: '',
        firstName: '',
        lastName: '',
        companyName: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'United Kingdom'
      },
      lineItems: [{
        description: '',
        quantity: 1,
        unitPrice: 0,
        taxRate: 0,
        discount: 0
      }],
      dueDate: '',
      currency: 'GBP',
      notes: '',
      poNumber: '',
      terms: ''
    });
    setAttachments([]);
    setStep(1);
    setRecipientType('user');
    setSelectedUser(null);
    setEarlyPaymentDiscount({
      enabled: false,
      discountAmount: '',
      discountPercentage: '',
      timeframeDays: 7
    });
    setCustomInvoiceNumber('');
    setInvoiceNumberError('');
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header with Progress */}
      <div className="bg-white sticky top-0 z-10 shadow-sm">
        <div className="p-4">
          <h1 className="text-xl font-bold text-gray-900 mb-4">Send Invoice</h1>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-4">
            {[1, 2, 3].map((num) => (
              <div key={num} className="flex items-center flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= num ? 'bg-[#1F11ED] text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {num}
                </div>
                {num < 3 && (
                  <div className={`flex-1 h-1 mx-2 ${step > num ? 'bg-[#1F11ED]' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
          
          <p className="text-sm text-gray-600 text-center">
            {step === 1 && 'Enter recipient details'}
            {step === 2 && 'Add invoice items'}
            {step === 3 && 'Set due date and send'}
          </p>
        </div>
      </div>

      {/* Form Content */}
      <div className="p-4">
        {/* Enhanced Error Display */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg flex items-start space-x-3 shadow-sm">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800">Error</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-600 hover:text-red-800 transition-colors flex-shrink-0"
              aria-label="Close error"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {businessWarnings.length > 0 && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-900">
            <p className="font-semibold mb-1">Complete your business profile</p>
            <p>Add your {businessWarnings.join(', ')} in Settings before sending an invoice.</p>
          </div>
        )}

        {/* Step 1: Recipient */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Recipient Type</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setRecipientType('user')}
                  className={`p-4 border-2 rounded-xl transition-all ${
                    recipientType === 'user' 
                      ? 'border-[#1F11ED] bg-blue-50' 
                      : 'border-gray-200'
                  }`}
                >
                  <User className="w-6 h-6 mx-auto mb-2 text-[#1F11ED]" />
                  <p className="text-sm font-medium">Receevit® User</p>
                </button>
                <button
                  onClick={() => {
                    setRecipientType('guest');
                    // Set default country to United Kingdom when switching to guest
                    setInvoice(prev => ({
                      ...prev,
                      recipient: {
                        ...prev.recipient,
                        country: prev.recipient.country || 'United Kingdom'
                      }
                    }));
                  }}
                  className={`p-4 border-2 rounded-xl transition-all ${
                    recipientType === 'guest' 
                      ? 'border-[#1F11ED] bg-blue-50' 
                      : 'border-gray-200'
                  }`}
                >
                  <Mail className="w-6 h-6 mx-auto mb-2 text-[#1F11ED]" />
                  <p className="text-sm font-medium">Guest</p>
                </button>
              </div>
            </div>

            {/* Guest Contact Search (only for guest type) */}
            {recipientType === 'guest' && (
              <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quick Fill from Contacts
                  <span className="block text-xs text-gray-500 mt-1">(Optional - or fill manually below)</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={guestContactSearch}
                    onChange={(e) => {
                      setGuestContactSearch(e.target.value);
                      searchGuestContacts(e.target.value);
                    }}
                    onFocus={() => {
                      if (guestContactResults.length > 0) setShowGuestContactDropdown(true);
                    }}
                    placeholder="Search your contacts..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  
                  {guestContactSearchLoading && (
                    <div className="absolute right-3 top-3.5">
                      <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                    </div>
                  )}
                  
                  {/* Contact search results dropdown */}
                  {showGuestContactDropdown && guestContactResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {guestContactResults.map((contact) => (
                        <button
                          key={contact.id}
                          type="button"
                          onClick={() => handleSelectGuestContact(contact)}
                          className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                        >
                          <p className="font-medium text-gray-900 text-sm">{contact.company_name}</p>
                          <p className="text-xs text-gray-600">{contact.first_name} {contact.last_name}</p>
                          <p className="text-xs text-gray-500">{contact.email}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
                {emailLookupLoading && <span className="ml-2 text-xs text-blue-600">Looking up...</span>}
              </label>
              <input
                type="email"
                value={invoice.recipient.email}
                onChange={(e) => {
                  const emailValue = e.target.value.trim();
                  console.log('📧 Mobile - Email input changed:', emailValue, 'Recipient type:', recipientType);
                  
                  setInvoice({
                    ...invoice,
                    recipient: { ...invoice.recipient, email: emailValue }
                  });
                  
                  // Clear selected user when email changes
                  if (selectedUser && selectedUser.email !== emailValue) {
                    console.log('🔄 Mobile - Clearing selected user, email changed');
                    setSelectedUser(null);
                  }
                  
                  // Trigger lookup after user stops typing (debounce)
                  if (window.emailLookupTimeout) {
                    clearTimeout(window.emailLookupTimeout);
                  }
                  
                  if (emailValue && recipientType === 'user') {
                    console.log('⏱️ Mobile - Setting timeout for email lookup:', emailValue);
                    window.emailLookupTimeout = setTimeout(() => {
                      console.log('🚀 Mobile - Timeout fired, calling lookupUserByEmail:', emailValue);
                      lookupUserByEmail(emailValue);
                    }, 800);
                  } else if (!emailValue) {
                    // Clear fields when email is empty
                    setSelectedUser(null);
                    setInvoice(prev => ({
                      ...prev,
                      recipient: {
                        ...prev.recipient,
                        companyName: '',
                        firstName: '',
                        lastName: '',
                        email: '',
                        // Keep address fields
                        address: prev.recipient.address,
                        city: prev.recipient.city,
                        state: prev.recipient.state,
                        zipCode: prev.recipient.zipCode,
                        country: prev.recipient.country
                      }
                    }));
                  }
                }}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#1F11ED] ${
                  selectedUser && recipientType === 'user' 
                    ? 'border-green-300 bg-green-50' 
                    : 'border-gray-300'
                }`}
                placeholder="recipient@example.com"
              />
              {selectedUser && recipientType === 'user' && (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                  <div className="flex items-center">
                    <Check className="w-4 h-4 mr-2" />
                    <span className="font-medium">Receevit® user found - details auto-filled!</span>
                  </div>
                </div>
              )}
              
              {/* Manual lookup button for Receevit® users */}
              {recipientType === 'user' && invoice.recipient.email && !selectedUser && !emailLookupLoading && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      console.log('🔍 Mobile - Manual lookup triggered for:', invoice.recipient.email);
                      lookupUserByEmail(invoice.recipient.email.trim());
                    }}
                    className="w-full px-4 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors font-medium"
                  >
                    🔍 Lookup User Details
                  </button>
                </div>
              )}
              
              {/* Fallback message when lookup fails */}
              {recipientType === 'user' && invoice.recipient.email && !selectedUser && !emailLookupLoading && (
                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                  <div className="flex items-start">
                    <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span>User not found. You can still send the invoice by filling in the details manually below.</span>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                <input
                  type="text"
                  value={invoice.recipient.firstName}
                  onChange={(e) => setInvoice({
                    ...invoice,
                    recipient: { ...invoice.recipient, firstName: e.target.value }
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                <input
                  type="text"
                  value={invoice.recipient.lastName}
                  onChange={(e) => setInvoice({
                    ...invoice,
                    recipient: { ...invoice.recipient, lastName: e.target.value }
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
              <input
                type="text"
                value={invoice.recipient.companyName}
                onChange={(e) => setInvoice({
                  ...invoice,
                  recipient: { ...invoice.recipient, companyName: e.target.value }
                })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
                placeholder="Company Ltd"
              />
            </div>

            {/* Recipient Address Section */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Recipient Address (Required)</h3>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2">Street Address *</label>
                  <input
                    type="text"
                    value={invoice.recipient.address}
                    onChange={(e) => setInvoice({
                      ...invoice,
                      recipient: { ...invoice.recipient, address: e.target.value }
                    })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
                    placeholder="123 Business Street"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">City *</label>
                    <input
                      type="text"
                      value={invoice.recipient.city}
                      onChange={(e) => setInvoice({
                        ...invoice,
                        recipient: { ...invoice.recipient, city: e.target.value }
                      })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
                      placeholder="London"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Postcode *</label>
                    <input
                      type="text"
                      value={invoice.recipient.zipCode}
                      onChange={(e) => setInvoice({
                        ...invoice,
                        recipient: { ...invoice.recipient, zipCode: e.target.value }
                      })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
                      placeholder="SW1A 1AA"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">State/County</label>
                    <input
                      type="text"
                      value={invoice.recipient.state}
                      onChange={(e) => setInvoice({
                        ...invoice,
                        recipient: { ...invoice.recipient, state: e.target.value }
                      })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
                      placeholder="England (optional)"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Country *</label>
                    <input
                      type="text"
                      value={invoice.recipient.country}
                      onChange={(e) => setInvoice({
                        ...invoice,
                        recipient: { ...invoice.recipient, country: e.target.value }
                      })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
                      placeholder="United Kingdom"
                    />
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => setStep(2)}
              disabled={!canProceedToStep2()}
              className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-[#1F11ED] text-white rounded-lg hover:bg-[#1709C7] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Next: Add Items</span>
              <ChevronRight className="w-5 h-5" />
            </button>
          </motion.div>
        )}

        {/* Step 2: Line Items */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
            <div className="space-y-3">
              {invoice.lineItems.map((item, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-gray-900">Item {index + 1}</h3>
                    {invoice.lineItems.length > 1 && (
                      <button
                        onClick={() => removeLineItem(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
                      placeholder="Service or product description"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Quantity *</label>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Unit Price (£) *</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unitPrice}
                        onChange={(e) => updateLineItem(index, 'unitPrice', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tax %</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={item.taxRate || 0}
                        onChange={(e) => updateLineItem(index, 'taxRate', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Discount %</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={item.discount || 0}
                        onChange={(e) => updateLineItem(index, 'discount', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">£{(item.quantity * item.unitPrice).toFixed(2)}</span>
                      </div>
                      {item.discount > 0 && (
                        <div className="flex justify-between text-red-600">
                          <span>Discount ({item.discount}%):</span>
                          <span>-£{((item.quantity * item.unitPrice) * (item.discount / 100)).toFixed(2)}</span>
                        </div>
                      )}
                      {item.taxRate > 0 && (
                        <div className="flex justify-between text-gray-600">
                          <span>Tax ({item.taxRate}%):</span>
                          <span>£{(((item.quantity * item.unitPrice) * (1 - (item.discount || 0) / 100)) * (item.taxRate / 100)).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex justify-between pt-2 mt-2 border-t border-gray-300">
                      <span className="text-sm font-semibold text-gray-900">Line Total:</span>
                      <span className="text-lg font-bold text-gray-900">
                        £{(() => {
                          const subtotal = item.quantity * item.unitPrice;
                          const discount = subtotal * ((item.discount || 0) / 100);
                          const afterDiscount = subtotal - discount;
                          const tax = afterDiscount * ((item.taxRate || 0) / 100);
                          return (afterDiscount + tax).toFixed(2);
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={addLineItem}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-[#1F11ED] hover:text-[#1F11ED]"
            >
              <Plus className="w-5 h-5" />
              <span>Add Another Item</span>
            </button>

            <div className="flex space-x-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!canProceedToStep3()}
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-[#1F11ED] text-white rounded-lg disabled:opacity-50"
              >
                <span>Next: Details</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Details & Send */}
        {step === 3 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">Due Date *</label>
              <input
                type="date"
                value={invoice.dueDate || ''}
                min={new Date().toISOString().split('T')[0]} // Set minimum date to today
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  
                  // Always update the value to allow user to type freely
                  // Validation will happen on blur
                  setInvoice({ ...invoice, dueDate: selectedDate });
                  
                  // Clear any previous errors when user starts typing
                  if (error && error.includes('Due date')) {
                    setError(null);
                  }
                }}
                onBlur={(e) => {
                  // Validate only when user finishes editing (on blur)
                  const selectedDate = e.target.value;
                  if (selectedDate) {
                    const today = new Date().toISOString().split('T')[0];
                    
                    if (selectedDate < today) {
                      setError('Due date cannot be in the past. Please select today or a future date.');
                    }
                  }
                }}
                className="w-full px-4 py-3 border-2 border-gray-400 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-[#1F11ED] bg-white text-gray-900 font-medium"
                style={{ colorScheme: 'light' }}
              />
              {!invoice.dueDate && (
                <p className="mt-1 text-xs text-red-600 font-medium">⚠️ Due date is required to send invoice</p>
              )}
              {invoice.dueDate && new Date(invoice.dueDate) < new Date(new Date().setHours(0, 0, 0, 0)) && (
                <p className="mt-1 text-xs text-red-600 font-medium">⚠️ Due date cannot be in the past</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Number *
              </label>
              <input
                type="text"
                value={customInvoiceNumber}
                onChange={(e) => handleCustomInvoiceNumberChange(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent ${
                  invoiceNumberError ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="INV-2025-0001"
                required
              />
              {invoiceNumberError && (
                <p className="text-sm text-red-600 mt-1">{invoiceNumberError}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Format: INV-YYYY-#### (e.g., INV-2025-0001)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">PO Number (Optional)</label>
              <input
                type="text"
                value={invoice.poNumber}
                onChange={(e) => setInvoice({ ...invoice, poNumber: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
                placeholder="PO-12345"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
              <textarea
                value={invoice.notes}
                onChange={(e) => setInvoice({ ...invoice, notes: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
                placeholder="Additional notes or special instructions..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Terms & Conditions (Optional)</label>
              <textarea
                value={invoice.terms}
                onChange={(e) => setInvoice({ ...invoice, terms: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
                placeholder="Payment terms, refund policy, etc..."
              />
            </div>

            {/* Attachments */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <Paperclip className="w-4 h-4 mr-2 text-gray-500" />
                    Attachments
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    PDF, DOC, XLS, JPG, PNG, TXT up to 10MB per file
                  </p>
                </div>
                <div>
                  <input
                    id="mobile-attachment-input"
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
                    className="hidden"
                    onChange={(e) => {
                      handleFileUpload(e.target.files);
                      e.target.value = '';
                    }}
                  />
                  <label
                    htmlFor="mobile-attachment-input"
                    className="inline-flex items-center px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-200 transition"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add files
                  </label>
                </div>
              </div>

              {attachmentError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg">
                  {attachmentError}
                </div>
              )}

              {attachments.length > 0 && (
                <div className="space-y-2">
                  {attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <File className="w-4 h-4 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{attachment.name}</p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(attachment.size)} • {attachment.type || 'file'}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(attachment.id)}
                        className="p-1 text-red-600 hover:text-red-800"
                        title="Remove attachment"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Early Payment Discount */}
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 flex items-center">
                  🎁 Early Payment Incentive
                </h3>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={earlyPaymentDiscount.enabled}
                    onChange={(e) => setEarlyPaymentDiscount({...earlyPaymentDiscount, enabled: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
              
              {earlyPaymentDiscount.enabled && (
                <div className="space-y-3 mt-3">
                  <p className="text-xs text-purple-700">Offer a discount if paid within a timeframe</p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Discount Amount (£)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={earlyPaymentDiscount.discountAmount}
                        onChange={(e) => setEarlyPaymentDiscount({...earlyPaymentDiscount, discountAmount: e.target.value, discountPercentage: ''})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                        placeholder="5.00"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">OR Percentage %</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={earlyPaymentDiscount.discountPercentage}
                        onChange={(e) => setEarlyPaymentDiscount({...earlyPaymentDiscount, discountPercentage: e.target.value, discountAmount: ''})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                        placeholder="5"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Timeframe (days)</label>
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={earlyPaymentDiscount.timeframeDays}
                      onChange={(e) => setEarlyPaymentDiscount({...earlyPaymentDiscount, timeframeDays: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Receiver must pay within this many days</p>
                  </div>
                </div>
              )}
            </div>

            {/* Invoice Summary */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Invoice Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Recipient:</span>
                  <span className="font-medium text-gray-900">{invoice.recipient.firstName} {invoice.recipient.lastName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Items:</span>
                  <span className="font-medium text-gray-900">{invoice.lineItems.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Due Date:</span>
                  <span className="font-medium text-gray-900">
                    {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('en-GB') : 'Not set'}
                  </span>
                </div>
                <div className="border-t border-blue-200 pt-2 mt-2 flex justify-between">
                  <span className="font-semibold text-gray-900">Total Amount:</span>
                  <span className="text-xl font-bold text-[#1F11ED]">£{calculateTotal().toFixed(2)}</span>
                </div>
              </div>
              
              {/* Minimum Amount Warning */}
              {(() => {
                const invoiceTotal = calculateTotal();
                const minCheck = checkStripeMinimum(invoiceTotal, invoice.currency);
                if (minCheck.isBelowMinimum) {
                  return (
                    <div className="mt-4 bg-red-50 border-2 border-red-300 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-red-900 mb-1">⚠️ Amount Below Minimum</p>
                          <p className="text-xs text-red-700">
                            {getMinimumAmountError(invoiceTotal, invoice.currency)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setStep(2)}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg"
              >
                Back
              </button>
              <button
                onClick={handleSendInvoice}
                disabled={!canSend() || loading}
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title={(() => {
                  const invoiceTotal = calculateTotal();
                  const minCheck = checkStripeMinimum(invoiceTotal, invoice.currency);
                  if (minCheck.isBelowMinimum) {
                    return getMinimumAmountError(invoiceTotal, invoice.currency);
                  }
                  return '';
                })()}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Send Invoice</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Success Modal */}
      <InvoiceSuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          resetForm();
        }}
        invoiceData={successInvoiceData}
        recipientName={
          recipientType === 'user' && selectedUser
            ? `${selectedUser.firstName} ${selectedUser.lastName}`
            : invoice.recipient.companyName || `${invoice.recipient.firstName} ${invoice.recipient.lastName}`
        }
        recipientEmail={invoice.recipient.email}
        onSendAnother={() => {
          setShowSuccessModal(false);
          resetForm();
        }}
      />
    </div>
  );
};

export default MobileSendInvoice;

